/**
 * AuthContext — Backward-compat layer over useAuthBootstrap state machine
 * =========================================================================
 * Eski mimari: 5 boolean (user/profile/loading/profileLoading/profileLoadError)
 * + 3 ayrı redirect noktası (Layout + AuthContext.login + usePostLoginRedirect)
 * → race condition → kullanıcı 5 firmalı olsa bile "ufak sorunda kayit-tamamla'ya
 * düşüyorum".
 *
 * Yeni mimari (2026-05-15):
 * - `useAuthBootstrap` SSoT state machine (frontend/src/application/auth/)
 * - `<AuthGate>` tek redirect karar mercii
 * - Bu dosya: eski 74 callsite'ın `user/profile/loading` field'larına erişimi
 *   için backward-compat. Yeni component'ler doğrudan `useAuthBootstrap` veya
 *   AuthGate kullanmalı.
 *
 * Eski API'yi koruyan field'lar:
 *   user, profile, loading, authLoading, profileLoading, profileLoadError,
 *   isAuthenticated, login, logout, googleLogin, register, getAccessToken,
 *   checkSession, fetchProfileData
 *
 * Eski 3 redirect noktası KALDIRILDI — AuthGate halleder.
 */

import { createContext, useContext, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { useRouter } from 'next/router';
import { supabase } from '@/shared/utils/supabaseClient';
import { useAuthBootstrap } from '@/application/auth/useAuthBootstrap';
// Auth altyapı hatası → ErrorLog (SSoT). Supabase axios-dışı → interceptor kör-noktası; bu kapatır.
import { reportAuthError } from '@/application/auth/reportAuthError';

// ─── Exported types ────────────────────────────────────────────────────────────

export type AuthProfile = ReturnType<typeof useAuthBootstrap>['profile'];
export type AuthError = ReturnType<typeof useAuthBootstrap>['error'];

export interface AuthUser {
  id: string;
  email: string | null;
  isim?: string;
  soyisim?: string;
  adSoyad?: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  profile: AuthProfile;
  loading: boolean;
  authLoading: boolean;
  profileLoading: boolean;
  profileLoadError: 'not_found' | 'rate_limit' | 'network' | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<unknown>;
  logout: () => Promise<void>;
  googleLogin: () => Promise<unknown>;
  register: (email: string, password: string, options?: { emailRedirectTo?: string }) => Promise<unknown>;
  getAccessToken: () => Promise<string>;
  checkSession: () => Promise<{ user: { id: string; email: string | null } } | null>;
  fetchProfileData: () => Promise<AuthProfile>;
  authState: string;
  authError: AuthError;
  firmaCount: number;
  singleFirmaId: string | null;
  refetchAuth: () => Promise<void>;
  setProfileLocal: (profile: AuthProfile) => void;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

// State machine → eski profileLoadError union'a map et (backward-compat)
function deriveProfileLoadError(
  state: string,
  error: AuthError
): 'not_found' | 'rate_limit' | 'network' | null {
  if (state === 'NEEDS_PROFILE') return 'not_found';
  if (state === 'TRANSIENT_ERR') {
    if (error?.code === 'RATE_LIMITED') return 'rate_limit';
    return 'network';
  }
  return null;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const router = useRouter();
  const auth = useAuthBootstrap();
  // Bootstrap callback'lerini ref'te tut — render-stable deps için.
  // Aksi halde auth object spread her render'da yeniden yaratılıyor → 74
  // callsite'taki useEffect([logout]) gibi deps'ler her tick yeniden bağlanırdı.
  const authRef = useRef(auth);
  authRef.current = auth;

  // ─── Backward-compat shape ─────────────────────────────────────────────────
  // Eski API: `user` Supabase auth user objesi (id + email + app_metadata).
  // Yeni state machine sadece userId+email tutar — minimal user object yarat.
  const user = useMemo<AuthUser | null>(() => {
    if (!auth.userId) return null;
    return { id: auth.userId, email: auth.email };
  }, [auth.userId, auth.email]);

  const profile = auth.profile;

  // Loading flags
  const loading = auth.state === 'BOOTING' || auth.state === 'CHECKING_SESSION';
  const profileLoading = auth.state === 'LOADING_PROFILE';
  const profileLoadError = deriveProfileLoadError(auth.state, auth.error);
  const isAuthenticated = !!auth.userId;

  // ─── Login / Logout / Register ──────────────────────────────────────────────

  /**
   * Email/password login. Sadece Supabase signIn yapar — redirect AuthGate'in
   * işi. SIGNED_IN event'i state machine'e tetiklenir, profile fetch otomatik
   * başlar, state READY/NEEDS_PROFILE/SUSPENDED'a evrilir, AuthGate o state'e
   * göre yönlendirir.
   */
  const login = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) { reportAuthError(error, 'login'); throw error; }
    // AuthGate'in default behavior'ı: state==READY + path==auth-route → /dashboard
    // ?next= override etmek isteniyorsa AuthGate kontrolünden geçer.
    return data;
  }, []);

  const googleLogin = useCallback(async () => {
    const baseUrl = typeof window !== 'undefined'
      ? (process.env.NEXT_PUBLIC_FRONTEND_URL || window.location.origin)
      : (process.env.NEXT_PUBLIC_FRONTEND_URL || 'https://www.riskmatik.com');
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${baseUrl}/auth/callback`,
        queryParams: { prompt: 'select_account' }
      }
    });
    if (error) { reportAuthError(error, 'googleLogin'); throw error; }
    return data;
  }, []);

  const register = useCallback(async (email: string, password: string, options: { emailRedirectTo?: string } = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      ...(options.emailRedirectTo ? { options: { emailRedirectTo: options.emailRedirectTo } } : {})
    });
    if (error) { reportAuthError(error, 'register'); throw error; }
    return data;
  }, []);

  // logout — state machine'in logout'unu çağırır + landing'e yönlendir.
  // authRef üzerinden okur (stable callback).
  const logout = useCallback(async () => {
    await authRef.current.logout();
    if (router.pathname !== '/') {
      if (typeof window !== 'undefined') {
        // window.location ile hard navigation: bfcache'i bypass eder (protected
        // sayfanın back-button ile gösterilmesini önler — KVKK m.12 koruması).
        window.location.replace('/');
      } else {
        router.push('/');
      }
    }
  }, [router]);

  // ─── getAccessToken (axios interceptor için) ────────────────────────────────

  const getAccessToken = useCallback(async () => {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    if (!data?.session) throw new Error('Oturum bulunamadı');
    return data.session.access_token;
  }, []);

  // ─── Backward-compat utilities ──────────────────────────────────────────────

  // checkSession — eski API. Sadece state machine'i refetch tetikler.
  // authRef üzerinden okur → callback referansı her render'da stable.
  const checkSession = useCallback(async () => {
    await authRef.current.refetch();
    const { userId, email } = authRef.current;
    return userId ? { user: { id: userId, email } } : null;
  }, []);

  // fetchProfileData — eski API. Refetch sonrası taze snapshot'tan profile döner.
  const fetchProfileData = useCallback(async () => {
    await authRef.current.refetch();
    return authRef.current.profile;
  }, []);

  const refetchAuth = useCallback(async () => {
    await authRef.current.refetch();
  }, []);

  const setProfileLocal = useCallback((profile: AuthProfile) => {
    authRef.current.setProfileLocal(profile);
  }, []);

  // value useMemo — auth state değişimine bağlı stable identity. 74 callsite'ın
  // useContext(AuthContext) re-render'ı sadece relevant field değişimde tetiklenir.
  const value = useMemo<AuthContextValue>(() => ({
    // Eski state (backward-compat)
    user,
    profile,
    loading,
    authLoading: loading,
    profileLoading,
    profileLoadError,
    isAuthenticated,
    // Eski metodlar
    login,
    logout,
    googleLogin,
    register,
    getAccessToken,
    checkSession,
    fetchProfileData,
    // Yeni API (kademeli adoption için expose)
    authState: auth.state,
    authError: auth.error,
    // AuthGate post-login redirect kararı için: state machine'in READY'ye girerken
    // yüklediği firma context'i (firmaCount, singleFirmaId) — resolver'a aktarılır
    firmaCount: auth.firmaCount,
    singleFirmaId: auth.singleFirmaId,
    refetchAuth,
    setProfileLocal
  }), [
    user, profile, loading, profileLoading, profileLoadError, isAuthenticated,
    login, logout, googleLogin, register, getAccessToken,
    checkSession, fetchProfileData, refetchAuth, setProfileLocal,
    auth.state, auth.error, auth.firmaCount, auth.singleFirmaId
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
